// packages/shared/src/services/product.service.ts
import {
  Product,
  ProductType,
  ProductFilters,
  ProductSortBy,
  COLLECTIONS,
} from '@bookstore/shared';
import { getFirebaseFirestore } from '@bookstore/shared/browser';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  doc,
  QueryConstraint,
  getCountFromServer,
  DocumentSnapshot,
  onSnapshot,
  documentId,
} from 'firebase/firestore';

export class ProductService {
  private get db() {
    return getFirebaseFirestore();
  }

  /**
   * Get ALL products (no pagination) for client-side search
   * Fetches all active products - cached by React Query for performance
   */
  async getAllProducts(): Promise<Product[]> {
    try {
      const productsQuery = query(
        collection(this.db, COLLECTIONS.PRODUCTS),
        where('isActive', '==', true),
        orderBy('title')
      );

      const snapshot = await getDocs(productsQuery);
      
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];
    } catch (error) {
      console.error('Error fetching all products:', error);
      throw error;
    }
  }

  /**
   * Get all products with filters and pagination
   */
  async getProducts(
    filters?: ProductFilters,
    sortBy: ProductSortBy = 'newest',
    pageSize: number = 20,
    lastDoc?: DocumentSnapshot
  ): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null; hasMore: boolean, totalCount?: number }> {
    try {
      const constraints: QueryConstraint[] = [];

      // Always filter active products
      constraints.push(where('isActive', '==', true));

      //  Filter by product type
      if (filters?.productType) {
        constraints.push(where('productType', '==', filters.productType));
      }

      // Apply other filters
      if (filters?.categoryId) {
        // Use array-contains for multi-category support
        constraints.push(where('categories', 'array-contains', filters.categoryId));
      }

      if (filters?.isFeatured !== undefined) {
        constraints.push(where('isFeatured', '==', filters.isFeatured));
      }

      if (filters?.inStock !== undefined && filters.inStock) {
        constraints.push(where('stock', '>', 0));
      }

      if (filters?.tags && filters.tags.length > 0) {
        constraints.push(where('tags', 'array-contains-any', filters.tags));
      }

      if (filters?.searchQuery) {
        const searchLower = filters.searchQuery.toLocaleLowerCase();
        constraints.push(where('searchKeywords', 'array-contains', searchLower));
      }

      // Apply sorting
      switch (sortBy) {
        case 'price-asc':
          constraints.push(orderBy('price', 'asc'));
          break;
        case 'price-desc':
          constraints.push(orderBy('price', 'desc'));
          break;
        case 'rating':
          constraints.push(orderBy('rating', 'desc'));
          break;
        case 'popular':
          constraints.push(orderBy('reviewCount', 'desc'));
          break;
        case 'newest':
        default:
          constraints.push(orderBy('createdAt', 'desc'));
          break;
      }

      // Get total count on first page only
    let totalCount: number | undefined;
    if (!lastDoc) {
      // Build count query (same filters, no limit, no startAfter)
      const countConstraints = constraints.filter(c => {
        const str = c.toString();
        return !str.includes('limit') && !str.includes('startAfter');
      });
      const countQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...countConstraints);
      const countSnapshot = await getCountFromServer(countQuery);
      totalCount = countSnapshot.data().count;
    }


      // Pagination
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      constraints.push(limit(pageSize));

      const productsQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...constraints);
      const snapshot = await getDocs(productsQuery);

      const products: Product[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === pageSize;

      return { products, lastDoc: lastVisible, hasMore, totalCount };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Get product by ID
   */
  async getProductById(productId: string): Promise<Product | null> {
    try {
      const productDoc = await getDoc(doc(this.db, COLLECTIONS.PRODUCTS, productId));
      if (!productDoc.exists()) {
        return null;
      }

      return { id: productDoc.id, ...productDoc.data() } as Product;
    } catch (error) {
      console.error('Error fetching product:', error);
      // Return null instead of throwing to gracefully handle deleted/inaccessible products
      return null;
    }
  }

  // Get multiple products by their IDs
async getProductsByIds(productIds: string[]): Promise<Product[]> {
  if (productIds.length === 0) return [];

  const db = this.db;
  const CHUNK_SIZE = 10;
  const chunks: string[][] = [];

  for (let i = 0; i < productIds.length; i += CHUNK_SIZE) {
    chunks.push(productIds.slice(i, i + CHUNK_SIZE));
  }

  const results: Product[] = [];

  for (const chunk of chunks) {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where(documentId(), 'in', chunk)
    );

    const snap = await getDocs(q);

    for (const docSnap of snap.docs) {
      results.push({ id: docSnap.id, ...docSnap.data() } as Product);
    }
  }

  return results;
}

  /**
   * Get product by SEO URL slug
   */
  async getProductBySlug(slug: string): Promise<Product | null> {
    try {
      const productsQuery = query(
        collection(this.db, COLLECTIONS.PRODUCTS),
        where('seoUrl', '==', slug),
        where('isActive', '==', true),
        limit(1)
      );

      const snapshot = await getDocs(productsQuery);

      if (snapshot.empty) {
        return null;
      }

      const productDoc = snapshot.docs[0];
      return { id: productDoc.id, ...productDoc.data() } as Product;
    } catch (error) {
      console.error('Error fetching product by slug:', error);
      throw error;
    }
  }

  /**
   * Search products by title, author, or description using searchKeywords
   * Supports both exact keyword matches and partial word matching
   */
  async searchProducts(searchQuery: string, productType?: ProductType, pageSize: number = 20): Promise<Product[]> {
    try {
      const searchLower = searchQuery.toLowerCase().trim();
      
      // If search query is empty, return empty results
      if (!searchLower) {
        return [];
      }

      // Split search query into words
      const searchWords = searchLower.split(/\s+/);
      const firstWord = searchWords[0];

      // First, try exact match using searchKeywords for efficiency
      const constraints: QueryConstraint[] = [
        where('isActive', '==', true),
        where('searchKeywords', 'array-contains', searchLower),
        orderBy('title'),
        limit(pageSize),
      ];

      //  Filter by product type if specified
      if (productType) {
        constraints.unshift(where('productType', '==', productType));
      }

      const productsQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...constraints);
      const snapshot = await getDocs(productsQuery);

      let products: Product[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      // If no exact matches found, try partial matching by fetching products with first word
      if (products.length === 0 && firstWord.length >= 2) {
        const partialConstraints: QueryConstraint[] = [
          where('isActive', '==', true),
          where('searchKeywords', 'array-contains', firstWord),
          limit(100), // Fetch more for client-side filtering
        ];

        if (productType) {
          partialConstraints.unshift(where('productType', '==', productType));
        }

        const partialQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...partialConstraints);
        const partialSnapshot = await getDocs(partialQuery);

        // Client-side filtering for partial matches
        products = partialSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((product: any) => {
            const titleLower = product.title.toLowerCase();
            const descLower = product.description?.toLowerCase() || '';
            
            // Check if all search words are present (allows partial word matching)
            return searchWords.every(word => 
              titleLower.includes(word) || descLower.includes(word)
            );
          })
          .slice(0, pageSize) as Product[];
      }

      return products;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Get featured products
   */
  async getFeaturedProducts(productType?: ProductType, pLimit: number = 8): Promise<Product[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('isActive', '==', true),
        where('isFeatured', '==', true),
        orderBy('createdAt', 'desc'),
        limit(pLimit),
      ];

      //  Filter by product type
      if (productType) {
        constraints.unshift(where('productType', '==', productType));
      }

      const productsQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...constraints);
      const snapshot = await getDocs(productsQuery);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(
    categoryId: string,
    productType?: ProductType,
    pageSize: number = 20,
    sortBy: ProductSortBy = 'newest'
  ): Promise<Product[]> {
    const { products } = await this.getProducts(
      { categoryId, productType },
      sortBy,
      pageSize
    );
    return products;
  }

  /**
   * Get related products (same category, excluding current product)
   */
  async getRelatedProducts(
    productId: string,
    categoryId: string,
    productType?: ProductType,
    pLimit: number = 4
  ): Promise<Product[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('isActive', '==', true),
        where('categories', 'array-contains', categoryId),
        orderBy('rating', 'desc'),
        limit(pLimit + 1),
      ];

      //  Filter by product type
      if (productType) {
        constraints.unshift(where('productType', '==', productType));
      }

      const productsQuery = query(collection(this.db, COLLECTIONS.PRODUCTS), ...constraints);
      const snapshot = await getDocs(productsQuery);

      const products: Product[] = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((product: any) => product.id !== productId)
        .slice(0, pLimit) as Product[];

      return products;
    } catch (error) {
      console.error('Error fetching related products:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time product updates
   */
  subscribeToProduct(productId: string, callback: (product: Product | null) => void): () => void {
    const ref = doc(this.db, COLLECTIONS.PRODUCTS, productId);
    const unsub = onSnapshot(ref, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() } as Product);
      } else {
        callback(null);
      }
    });
    return unsub;
  }

  // ============================================
  // Backward Compatibility Methods (Books)
  // ============================================
  
  async getBooks(...args: Parameters<typeof this.getProducts>) {
    const [filters, ...rest] = args;
    return this.getProducts({ ...filters, productType: ProductType.BOOK }, ...rest);
  }

  async getBookById(bookId: string) {
    return this.getProductById(bookId);
  }

  async getBookBySlug(slug: string) {
    return this.getProductBySlug(slug);
  }

  async searchBooks(searchQuery: string, pageSize?: number) {
    return this.searchProducts(searchQuery, ProductType.BOOK, pageSize);
  }

  async getFeaturedBooks(bLimit?: number) {
    return this.getFeaturedProducts(ProductType.BOOK, bLimit);
  }

  async getBooksByCategory(categoryId: string, pageSize?: number, sortBy?: ProductSortBy) {
    return this.getProductsByCategory(categoryId, ProductType.BOOK, pageSize, sortBy);
  }

  async getRelatedBooks(bookId: string, categoryId: string, bLimit?: number) {
    return this.getRelatedProducts(bookId, categoryId, ProductType.BOOK, bLimit);
  }

  subscribeToBook(bookId: string, callback: (book: Product | null) => void) {
    return this.subscribeToProduct(bookId, callback);
  }
}

// Export singleton
export const productService = new ProductService();

// Backward compatibility
export const bookService = productService;